/**
 * PRODUCT UPSELLS — Sport/team-based upsell carousel (Web Component)
 * Two-tier matching: Tier 1 = same team, Tier 2 = same sport
 */
(function () {
  'use strict';

  var CART_DRAWER_SECTION = 'cart-drawer';
  var CART_DRAWER_TAG = 'cart-drawer';
  var BATCH_SIZE = 8;

  /* ---- Shared caches (static across instances, session-scoped) ---- */
  var _configCache = null;
  var _configPromise = null;
  var _allProductsCache = null;
  var _allProductsPromise = null;
  var _cartVariantIds = new Set();

  /* ---- Helpers ---- */
  function esc(str) {
    var d = document.createElement('div');
    d.textContent = str || '';
    return d.innerHTML;
  }

  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat(
        (typeof Shopify !== 'undefined' && Shopify.locale) || 'en',
        {
          style: 'currency',
          currency:
            (typeof Shopify !== 'undefined' &&
              Shopify.currency &&
              Shopify.currency.active) ||
            'USD',
        }
      ).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function resizeImg(url, size) {
    if (!url) return '';
    /* Use Shopify CDN width parameter (works with all CDN URL formats) */
    var w = parseInt(size, 10);
    if (!w) return url;
    if (url.indexOf('?') !== -1) {
      return url + '&width=' + w;
    }
    return url + '?width=' + w;
  }

  function fetchConfig(url, signal) {
    if (_configCache) return Promise.resolve(_configCache);
    if (_configPromise) return _configPromise;
    _configPromise = fetch(url, { signal: signal })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        _configCache = data;
        return data;
      });
    return _configPromise;
  }

  function fetchAllProducts(signal) {
    if (_allProductsCache) return Promise.resolve(_allProductsCache);
    if (_allProductsPromise) return _allProductsPromise;

    var all = [];
    function fetchPage(page) {
      var root =
        typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
          ? Shopify.routes.root
          : '/';
      return fetch(root + 'products.json?limit=250&page=' + page, {
        signal: signal,
      })
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var products = data.products || [];
          products.forEach(function (p) {
            /* normalize featured_image (may be string, object, or falsy) */
            if (p.featured_image && typeof p.featured_image === 'object') {
              p.featured_image = p.featured_image.src || '';
            }
            if (!p.featured_image) {
              if (p.image && p.image.src) {
                p.featured_image = p.image.src;
              } else if (p.images && p.images.length) {
                var first = p.images[0];
                p.featured_image = (typeof first === 'object') ? (first.src || '') : (first || '');
              }
            }
            /* normalize images array */
            if (p.images && p.images.length && typeof p.images[0] === 'object') {
              p.images = p.images.map(function (img) {
                return img.src || img;
              });
            }
            /* normalize variant prices */
            (p.variants || []).forEach(function (v) {
              if (typeof v.price === 'string') {
                v.price = Math.round(parseFloat(v.price) * 100);
              }
              if (
                v.compare_at_price &&
                typeof v.compare_at_price === 'string'
              ) {
                v.compare_at_price = Math.round(
                  parseFloat(v.compare_at_price) * 100
                );
              }
            });
          });
          all = all.concat(products);
          if (products.length === 250) {
            return fetchPage(page + 1);
          }
          return all;
        });
    }

    _allProductsPromise = fetchPage(1).then(function (products) {
      _allProductsCache = products;
      return products;
    });
    return _allProductsPromise;
  }

  function fetchCartVariants(signal) {
    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : '/';
    return fetch(root + 'cart.js', { signal: signal })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        _cartVariantIds = new Set();
        (cart.items || []).forEach(function (item) {
          _cartVariantIds.add(item.variant_id);
        });
        return _cartVariantIds;
      });
  }

  function refreshCartDrawer() {
    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : '/';
    setTimeout(function () {
      fetch(root + '?sections=' + CART_DRAWER_SECTION)
        .then(function (r) {
          return r.json();
        })
        .then(function (data) {
          var freshHtml = data[CART_DRAWER_SECTION];
          if (!freshHtml) return;
          var temp = document.createElement('div');
          temp.innerHTML = freshHtml;
          var newDrawer = temp.querySelector(CART_DRAWER_TAG);
          var currentDrawer = document.querySelector(CART_DRAWER_TAG);
          if (newDrawer && currentDrawer) {
            currentDrawer.innerHTML = newDrawer.innerHTML;
          }
        })
        .catch(function (err) {
          console.warn('[product-upsells] Cart drawer refresh error:', err);
        });
    }, 300);
  }

  /* ---- Web Component ---- */
  var ProductUpsells = function () {
    var el = Reflect.construct(HTMLElement, [], ProductUpsells);
    return el;
  };
  ProductUpsells.prototype = Object.create(HTMLElement.prototype);
  ProductUpsells.prototype.constructor = ProductUpsells;

  ProductUpsells.prototype.connectedCallback = function () {
    if (this._skipReconnect) return;
    if (this._initialized) return;

    this._handle = this.getAttribute('product-handle');
    this._configUrl = this.getAttribute('config-url');
    if (!this._handle || !this._configUrl) return;

    this._initialized = true;
    this._abortController = new AbortController();
    this._batchSize = BATCH_SIZE;
    this._renderedCount = 0;
    this._allProducts = [];
    this._observer = null;
    this._wasDragged = false;

    this._init();
  };

  ProductUpsells.prototype.disconnectedCallback = function () {
    if (this._skipReconnect) return;
    if (this._abortController) this._abortController.abort();
    if (this._observer) this._observer.disconnect();
    this._removeCartListener();
    this._removeDragListeners();
    this._initialized = false;
  };

  ProductUpsells.prototype._removeCartListener = function () {
    if (this._cartHandler) {
      document.documentElement.removeEventListener(
        'cart:updated',
        this._cartHandler
      );
      this._cartHandler = null;
    }
  };

  ProductUpsells.prototype._removeDragListeners = function () {
    if (this._docMouseMove) {
      document.removeEventListener('mousemove', this._docMouseMove);
    }
    if (this._docMouseUp) {
      document.removeEventListener('mouseup', this._docMouseUp);
    }
  };

  ProductUpsells.prototype._init = function () {
    var self = this;
    var signal = this._abortController.signal;

    Promise.all([
      fetchConfig(this._configUrl, signal),
      fetchCartVariants(signal),
      fetchAllProducts(signal),
    ])
      .then(function (results) {
        var config = results[0];
        var allProducts = results[2];
        var match = self._findMatch(config);
        if (!match) return;

        /* Build tier 1 + tier 2 */
        var currentHandle = self._handle;
        var teamKeywords = match.team.keywords;
        var sportTeams = match.sport.teams;
        var allSportKeywords = [];
        sportTeams.forEach(function (t) {
          allSportKeywords = allSportKeywords.concat(t.keywords);
        });

        var tier1 = [];
        var tier2 = [];
        var tier1Handles = {};

        allProducts.forEach(function (p) {
          if (p.handle === currentHandle) return;
          var handle = p.handle;
          var isTeam = teamKeywords.some(function (kw) {
            return handle.indexOf(kw) !== -1;
          });
          if (isTeam) {
            tier1.push(p);
            tier1Handles[handle] = true;
          }
        });

        allProducts.forEach(function (p) {
          if (p.handle === currentHandle) return;
          if (tier1Handles[p.handle]) return;
          var handle = p.handle;
          var isSport = allSportKeywords.some(function (kw) {
            return handle.indexOf(kw) !== -1;
          });
          if (isSport) {
            tier2.push(p);
          }
        });

        /* Sort helper: available first, in-cart second, sold-out last */
        function sortProducts(arr) {
          return arr.sort(function (a, b) {
            var aState = self._productState(a);
            var bState = self._productState(b);
            return aState - bState;
          });
        }

        var combined = tier1.concat(tier2);
        if (combined.length === 0) return;

        /* Sort entire list: available first, in-cart second, sold-out last */
        combined = sortProducts(combined);

        /* Heading always uses sport name (carousel shows mixed teams from the sport) */
        var heading = match.sport.heading || config.settings.fallback_heading;

        self._allProducts = combined;
        self._heading = heading;
        self._renderShell();
        self._renderBatch();
        self.setAttribute('data-loaded', '');
        self._setupLazyLoad();
        self._setupDrag();
        self._setupDragHint();
        self._setupCardEvents();

        /* Desktop full-width repositioning (4+ products, >= 1000px) */
        if (combined.length >= 4 && window.innerWidth >= 1000) {
          self._repositionFullWidth();
        }

        /* Listen for cart changes */
        self._cartHandler = function () {
          self._handleCartChange();
        };
        document.documentElement.addEventListener(
          'cart:updated',
          self._cartHandler
        );
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') {
          console.warn('[product-upsells] Init error:', err);
        }
      });
  };

  ProductUpsells.prototype._productState = function (product) {
    var variants = product.variants || [];
    var anyAvailable = variants.some(function (v) {
      return v.available;
    });
    if (!anyAvailable) return 2; /* sold out */
    var anyInCart = variants.some(function (v) {
      return _cartVariantIds.has(v.id);
    });
    if (anyInCart) return 1; /* in cart */
    return 0; /* available */
  };

  ProductUpsells.prototype._findMatch = function (config) {
    var handle = this._handle;
    var sports = config.sports || [];
    for (var s = 0; s < sports.length; s++) {
      var sport = sports[s];
      var teams = sport.teams || [];
      for (var t = 0; t < teams.length; t++) {
        var team = teams[t];
        var keywords = team.keywords || [];
        for (var k = 0; k < keywords.length; k++) {
          if (handle.indexOf(keywords[k]) !== -1) {
            return { team: team, sport: sport };
          }
        }
      }
    }
    return null;
  };

  ProductUpsells.prototype._renderShell = function () {
    this.innerHTML =
      '<div class="upsell">' +
      '<div class="upsell__header">' +
      '<h3 class="upsell__heading">' +
      esc(this._heading) +
      '</h3>' +
      '</div>' +
      '<div class="upsell__grid"></div>' +
      '<div class="upsell__drag-hint">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="15 18 9 12 15 6"/></svg>' +
      '<span>Drag to explore</span>' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="9 18 15 12 9 6"/></svg>' +
      '</div>' +
      '</div>';
    this._grid = this.querySelector('.upsell__grid');
  };

  ProductUpsells.prototype._renderBatch = function () {
    var start = this._renderedCount;
    var end = Math.min(start + this._batchSize, this._allProducts.length);
    var fragment = document.createDocumentFragment();

    for (var i = start; i < end; i++) {
      var p = this._allProducts[i];
      var card = this._buildCard(p, i);
      fragment.appendChild(card);
    }

    /* Remove existing sentinel before appending */
    var existingSentinel = this._grid.querySelector('.upsell__sentinel');
    if (existingSentinel) existingSentinel.remove();

    this._grid.appendChild(fragment);
    this._renderedCount = end;

    /* Add sentinel if more to load */
    if (this._renderedCount < this._allProducts.length) {
      var sentinel = document.createElement('div');
      sentinel.className = 'upsell__sentinel';
      this._grid.appendChild(sentinel);
    }
  };

  ProductUpsells.prototype._buildCard = function (product, index) {
    var variants = product.variants || [];
    var state = this._productState(product);
    var stateClass = '';
    if (state === 1) stateClass = ' upsell__card--in-cart';
    if (state === 2) stateClass = ' upsell__card--sold-out';

    /* Pick preferred variant */
    var selected = this._pickVariant(variants);
    var price = selected ? selected.price : 0;
    var comparePrice =
      selected && selected.compare_at_price && selected.compare_at_price > price
        ? selected.compare_at_price
        : 0;

    var imgSrc = product.featured_image
      ? resizeImg(product.featured_image, '400x')
      : '';
    var delay = Math.min(index * 0.06, 0.6);
    var productUrl =
      (typeof Shopify !== 'undefined' &&
      Shopify.routes &&
      Shopify.routes.root
        ? Shopify.routes.root
        : '/') +
      'products/' +
      product.handle;

    var html =
      '<a href="' +
      esc(productUrl) +
      '" class="upsell__card-image">' +
      (imgSrc
        ? '<img src="' +
          esc(imgSrc) +
          '" alt="' +
          esc(product.title) +
          '" loading="lazy" width="400" height="500" />'
        : '') +
      '</a>' +
      '<div class="upsell__card-info">' +
      '<a href="' +
      esc(productUrl) +
      '" class="upsell__card-title">' +
      esc(product.title) +
      '</a>' +
      '<div class="upsell__card-price">' +
      (comparePrice
        ? '<span class="upsell__card-compare">' +
          formatMoney(comparePrice) +
          '</span>'
        : '') +
      '<span>' +
      formatMoney(price) +
      '</span>' +
      '</div>' +
      this._buildSizePills(variants, selected) +
      this._buildAddButton(selected, state) +
      '</div>';

    var div = document.createElement('div');
    div.className = 'upsell__card' + stateClass;
    div.setAttribute('data-handle', product.handle);
    div.style.animationDelay = delay + 's';
    div.innerHTML = html;
    return div;
  };

  ProductUpsells.prototype._pickVariant = function (variants) {
    /* Prefer first available NOT in cart */
    var available = variants.filter(function (v) {
      return v.available;
    });
    var notInCart = available.filter(function (v) {
      return !_cartVariantIds.has(v.id);
    });
    if (notInCart.length) return notInCart[0];
    if (available.length) return available[0];
    return variants[0] || null;
  };

  ProductUpsells.prototype._buildSizePills = function (variants, selected) {
    if (!variants || variants.length <= 1) return '';
    var html = '<div class="upsell__sizes">';
    variants.forEach(function (v) {
      var name = v.title || v.option1 || '';
      var cls = 'upsell__size';
      var disabled = '';
      if (!v.available) {
        cls += ' upsell__size--disabled';
        disabled = ' disabled';
      } else if (selected && v.id === selected.id) {
        cls += ' upsell__size--selected';
      }
      html +=
        '<button class="' +
        cls +
        '" data-variant-id="' +
        v.id +
        '" data-price="' +
        (v.price || 0) +
        '" data-compare-price="' +
        (v.compare_at_price || 0) +
        '"' +
        disabled +
        '>' +
        esc(name) +
        '</button>';
    });
    html += '</div>';
    return html;
  };

  ProductUpsells.prototype._buildAddButton = function (variant, state) {
    if (state === 2) {
      return (
        '<button class="upsell__add-btn upsell__add-btn--sold-out" disabled>' +
        '<span class="upsell__add-text">Sold Out</span>' +
        '</button>'
      );
    }
    var inCart = variant && _cartVariantIds.has(variant.id);
    var btnClass = 'upsell__add-btn';
    var text = '+ Add';
    var dis = '';
    if (inCart) {
      btnClass += ' upsell__add-btn--in-cart';
      text = 'In Cart';
      dis = ' disabled';
    }
    return (
      '<button class="' +
      btnClass +
      '" data-variant-id="' +
      (variant ? variant.id : '') +
      '"' +
      dis +
      '>' +
      '<span class="upsell__add-text">' +
      text +
      '</span>' +
      '<span class="upsell__add-loading" hidden>' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>' +
      '</span>' +
      '<span class="upsell__add-done" hidden>' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' +
      ' Added!' +
      '</span>' +
      '</button>'
    );
  };

  /* ---- Lazy Loading ---- */
  ProductUpsells.prototype._setupLazyLoad = function () {
    var self = this;
    if (this._renderedCount >= this._allProducts.length) return;

    this._observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            self._observer.unobserve(entry.target);
            self._renderBatch();
            if (self._renderedCount >= self._allProducts.length) {
              self._observer.disconnect();
            } else {
              var newSentinel = self._grid.querySelector('.upsell__sentinel');
              if (newSentinel) self._observer.observe(newSentinel);
            }
          }
        });
      },
      { rootMargin: '300px' }
    );

    var sentinel = this._grid.querySelector('.upsell__sentinel');
    if (sentinel) this._observer.observe(sentinel);
  };

  /* ---- Drag to Scroll ---- */
  ProductUpsells.prototype._setupDrag = function () {
    var self = this;
    var grid = this._grid;
    var startX, scrollStart, isDragging, threshold;

    grid.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      startX = e.pageX;
      scrollStart = grid.scrollLeft;
      isDragging = false;
      threshold = false;

      self._docMouseMove = function (ev) {
        var dx = ev.pageX - startX;
        if (!threshold && Math.abs(dx) < 5) return;
        threshold = true;
        if (!isDragging) {
          isDragging = true;
          grid.classList.add('upsell__grid--grabbing');
        }
        grid.scrollLeft = scrollStart - dx;
      };

      self._docMouseUp = function () {
        document.removeEventListener('mousemove', self._docMouseMove);
        document.removeEventListener('mouseup', self._docMouseUp);
        if (isDragging) {
          grid.classList.remove('upsell__grid--grabbing');
          self._wasDragged = true;
          setTimeout(function () {
            self._wasDragged = false;
          }, 50);
        }
      };

      document.addEventListener('mousemove', self._docMouseMove);
      document.addEventListener('mouseup', self._docMouseUp);
    });

    grid.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    grid.addEventListener('click', function (e) {
      if (self._wasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  };

  /* ---- Drag Hint ---- */
  ProductUpsells.prototype._setupDragHint = function () {
    var hint = this.querySelector('.upsell__drag-hint');
    if (!hint) return;
    var grid = this._grid;
    var hidden = false;
    function hideHint() {
      if (hidden) return;
      hidden = true;
      hint.classList.add('upsell__drag-hint--hidden');
    }
    grid.addEventListener('mousedown', hideHint, { once: true });
    grid.addEventListener('scroll', hideHint, { once: true });
    grid.addEventListener('touchstart', hideHint, { once: true, passive: true });
  };

  /* ---- Card Events (delegation) ---- */
  ProductUpsells.prototype._setupCardEvents = function () {
    var self = this;
    this._grid.addEventListener('click', function (e) {
      /* Size pill click */
      var pill = e.target.closest('.upsell__size:not(.upsell__size--disabled)');
      if (pill) {
        e.preventDefault();
        self._handleSizePillClick(pill);
        return;
      }
      /* Add button click */
      var addBtn = e.target.closest('.upsell__add-btn');
      if (
        addBtn &&
        !addBtn.disabled &&
        !addBtn.classList.contains('upsell__add-btn--in-cart') &&
        !addBtn.classList.contains('upsell__add-btn--sold-out')
      ) {
        e.preventDefault();
        self._handleAddToCart(addBtn);
      }
    });
  };

  ProductUpsells.prototype._handleSizePillClick = function (pill) {
    var card = pill.closest('.upsell__card');
    if (!card) return;
    var pills = card.querySelectorAll('.upsell__size');
    pills.forEach(function (p) {
      p.classList.remove('upsell__size--selected');
    });
    pill.classList.add('upsell__size--selected');

    var variantId = pill.getAttribute('data-variant-id');
    var price = parseInt(pill.getAttribute('data-price'), 10) || 0;
    var comparePrice =
      parseInt(pill.getAttribute('data-compare-price'), 10) || 0;

    /* Update add button */
    var btn = card.querySelector('.upsell__add-btn');
    if (btn) {
      btn.setAttribute('data-variant-id', variantId);
      if (_cartVariantIds.has(parseInt(variantId, 10))) {
        btn.classList.add('upsell__add-btn--in-cart');
        btn.disabled = true;
        var txt = btn.querySelector('.upsell__add-text');
        if (txt) txt.textContent = 'In Cart';
      } else {
        btn.classList.remove('upsell__add-btn--in-cart');
        btn.disabled = false;
        var txt2 = btn.querySelector('.upsell__add-text');
        if (txt2) txt2.textContent = '+ Add';
      }
    }

    /* Update price */
    var priceEl = card.querySelector('.upsell__card-price');
    if (priceEl) {
      var html = '';
      if (comparePrice && comparePrice > price) {
        html +=
          '<span class="upsell__card-compare">' +
          formatMoney(comparePrice) +
          '</span>';
      }
      html += '<span>' + formatMoney(price) + '</span>';
      priceEl.innerHTML = html;
    }
  };

  ProductUpsells.prototype._handleAddToCart = function (btn) {
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    if (!variantId) return;

    var textEl = btn.querySelector('.upsell__add-text');
    var loadingEl = btn.querySelector('.upsell__add-loading');
    var doneEl = btn.querySelector('.upsell__add-done');
    var card = btn.closest('.upsell__card');

    /* Busy state */
    btn.disabled = true;
    if (textEl) textEl.hidden = true;
    if (loadingEl) loadingEl.hidden = false;
    if (doneEl) doneEl.hidden = true;

    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : '/';

    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Add to cart failed');
        return r.json();
      })
      .then(function () {
        /* Fetch fresh cart */
        return fetch(root + 'cart.js').then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        /* Update cart badge */
        var counts = document.querySelectorAll('cart-count');
        counts.forEach(function (el) {
          el.textContent = cart.item_count;
          el.removeAttribute('hidden');
        });

        /* Dispatch events */
        document.documentElement.dispatchEvent(
          new CustomEvent('product-ajax:added', {
            bubbles: true,
            detail: { blockCartDrawerOpening: true },
          })
        );
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:updated', { bubbles: true })
        );

        /* Section Rendering API refresh (Bug 1 fix) */
        refreshCartDrawer();

        /* Track in set */
        _cartVariantIds.add(variantId);

        /* Success state */
        if (loadingEl) loadingEl.hidden = true;
        if (doneEl) doneEl.hidden = false;
        btn.classList.add('upsell__add-btn--success');

        /* Settle into "In Cart" after 2s */
        setTimeout(function () {
          btn.classList.remove('upsell__add-btn--success');
          btn.classList.add('upsell__add-btn--in-cart');
          if (doneEl) doneEl.hidden = true;
          if (textEl) {
            textEl.textContent = 'In Cart';
            textEl.hidden = false;
          }
          if (card) card.classList.add('upsell__card--in-cart');
        }, 2000);
      })
      .catch(function (err) {
        console.warn('[product-upsells] Add to cart error:', err);
        /* Revert */
        btn.disabled = false;
        if (loadingEl) loadingEl.hidden = true;
        if (doneEl) doneEl.hidden = true;
        if (textEl) {
          textEl.textContent = '+ Add';
          textEl.hidden = false;
        }
      });
  };

  /* ---- Full-width repositioning ---- */
  ProductUpsells.prototype._repositionFullWidth = function () {
    var productGrid = this.closest('.product');
    if (!productGrid) return;

    this._skipReconnect = true;
    productGrid.after(this);
    this._skipReconnect = false;
    this.setAttribute('data-full-width', '');
  };

  /* ---- Cart change handler ---- */
  ProductUpsells.prototype._handleCartChange = function () {
    var self = this;
    var root =
      typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root
        ? Shopify.routes.root
        : '/';
    fetch(root + 'cart.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        _cartVariantIds = new Set();
        (cart.items || []).forEach(function (item) {
          _cartVariantIds.add(item.variant_id);
        });
        /* Update card states */
        var cards = self.querySelectorAll('.upsell__card');
        cards.forEach(function (card) {
          var btn = card.querySelector('.upsell__add-btn');
          if (!btn) return;
          var vid = parseInt(btn.getAttribute('data-variant-id'), 10);
          if (_cartVariantIds.has(vid)) {
            card.classList.add('upsell__card--in-cart');
            btn.classList.add('upsell__add-btn--in-cart');
            btn.disabled = true;
            var txt = btn.querySelector('.upsell__add-text');
            if (txt) {
              txt.textContent = 'In Cart';
              txt.hidden = false;
            }
          }
        });
      })
      .catch(function (err) {
        console.warn('[product-upsells] Cart change error:', err);
      });
  };

  customElements.define('product-upsells', ProductUpsells);
})();
